import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { LiveMap } from "@/components/LiveMap";
import { ShieldCheck, MapPin, Clock, BellRing, Navigation, Check, BatteryLow, WifiOff, Copy, ExternalLink, LocateFixed } from "lucide-react";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  return `${Math.floor(s / 3600)} h ago`;
};

// Coloured freshness state for the recipient (req. A).
const freshness = (iso, hasLoc) => {
  if (!hasLoc) return { color: "#DC2626", label: "Location temporarily unavailable" };
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 20) return { color: "#16A34A", label: "Live" };
  if (s < 60) return { color: "#CA8A04", label: `Last updated ${s}s ago` };
  if (s < 600) return { color: "#EA580C", label: `Last updated ${Math.floor(s / 60)} min ago` };
  return { color: "#DC2626", label: "Location temporarily unavailable" };
};

const haversineKm = (aLat, aLng, bLat, bLng) => {
  const R = 6371;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLng = ((bLng - aLng) * Math.PI) / 180;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos((aLat * Math.PI) / 180) * Math.cos((bLat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
};

// Read-only recipient location panel: coordinates, copy, Google Maps, distance.
const LocationPanel = ({ loc, lastUpdate, travellerName }) => {
  const [origin, setOrigin] = useState(null);
  const [dist, setDist] = useState(null);
  const [distErr, setDistErr] = useState("");
  const [calc, setCalc] = useState(false);

  const fresh = freshness(lastUpdate, !!loc);
  const mapsView = loc ? `https://www.google.com/maps/search/?api=1&query=${loc.lat},${loc.lng}` : "#";
  const mapsDir = loc
    ? `https://www.google.com/maps/dir/?api=1&destination=${loc.lat},${loc.lng}` + (origin ? `&origin=${origin.lat},${origin.lng}` : "")
    : "#";

  const copyCoords = () => {
    if (!loc) return;
    const txt = `${loc.lat}, ${loc.lng}`;
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(txt).then(() => toast.success("Coordinates copied")).catch(() => window.prompt("Copy coordinates:", txt));
    else window.prompt("Copy coordinates:", txt);
  };

  const calcDistance = () => {
    setDistErr("");
    if (!navigator.geolocation) { setDistErr("Location isn't available on this device — use Open in Google Maps instead."); return; }
    setCalc(true);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setCalc(false);
        setOrigin({ lat: p.coords.latitude, lng: p.coords.longitude });
        if (loc) setDist(haversineKm(p.coords.latitude, p.coords.longitude, loc.lat, loc.lng));
      },
      () => { setCalc(false); setDistErr("Location permission denied — you can still tap Navigate to open Google Maps."); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <div className="bg-white rounded-3xl p-5 border border-[#111827]/5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="share-location-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-bold uppercase tracking-wide text-[#111827]/45">Live location</p>
        <span className="flex items-center gap-1.5 text-xs font-bold" data-testid="share-staleness" style={{ color: fresh.color }}>
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: fresh.color }} /> {fresh.label}
        </span>
      </div>

      {loc ? (
        <>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wide text-[#111827]/40">Latitude</p>
              <p className="font-mono font-bold text-sm" data-testid="share-lat">{loc.lat}</p>
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wide text-[#111827]/40">Longitude</p>
              <p className="font-mono font-bold text-sm" data-testid="share-lng">{loc.lng}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button data-testid="share-copy-coords" onClick={copyCoords} className="flex items-center justify-center gap-1.5 rounded-2xl py-2.5 bg-[#111827]/5 text-[#111827] text-sm font-bold active:scale-95 transition-transform">
              <Copy className="w-4 h-4" /> Copy coordinates
            </button>
            <a data-testid="share-open-maps" href={mapsView} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 rounded-2xl py-2.5 bg-[#111827]/5 text-[#111827] text-sm font-bold active:scale-95 transition-transform">
              <ExternalLink className="w-4 h-4" /> Open in Google Maps
            </a>
          </div>

          <a data-testid="share-navigate" href={mapsDir} target="_blank" rel="noreferrer" className="mt-2 flex items-center justify-center gap-2 rounded-2xl py-3 bg-[#0E7C66] text-white font-black active:scale-[0.98] transition-transform">
            <Navigation className="w-5 h-5" /> Navigate to {travellerName}
          </a>

          {dist == null ? (
            <button data-testid="share-calc-distance" onClick={calcDistance} disabled={calc} className="mt-2 w-full flex items-center justify-center gap-2 rounded-2xl py-2.5 border border-[#0E7C66]/30 text-[#0E7C66] text-sm font-bold active:scale-95 transition-transform disabled:opacity-60">
              <LocateFixed className="w-4 h-4" /> {calc ? "Getting your location…" : "Calculate my distance"}
            </button>
          ) : (
            <div className="mt-3 bg-[#0E7C66]/8 rounded-2xl p-3 text-sm" data-testid="share-distance">
              <p className="font-bold text-[#0E7C66]">~{dist.toFixed(2)} km away <span className="font-normal text-[#111827]/50">(straight line)</span></p>
              <a href={mapsDir} target="_blank" rel="noreferrer" className="text-xs font-semibold text-[#0E7C66] underline">Open Google Maps for walking / driving ETA</a>
            </div>
          )}
          {distErr && <p className="mt-2 text-xs text-[#92400e] bg-[#F59E0B]/12 rounded-xl p-2.5" data-testid="share-distance-error">{distErr}</p>}
        </>
      ) : (
        <div className="text-center py-4" data-testid="share-loc-unavailable">
          <MapPin className="w-6 h-6 mx-auto mb-2 text-[#DC2626]/50" />
          <p className="text-sm text-[#111827]/55">Location temporarily unavailable — waiting for the traveller's GPS.</p>
        </div>
      )}
    </div>
  );
};

export default function JourneyShare() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const [data, setData] = useState(undefined); // undefined=loading, object, or {error}
  const [remainingS, setRemainingS] = useState(null);
  const [, forceTick] = useState(0);

  // Acknowledge + mark-read the alert when a trusted contact opens the link.
  useEffect(() => {
    const a = sp.get("a");
    if (a) {
      api.post(`/journeys/alert/${a}/ack`).catch(() => {});
      api.post(`/journeys/alert/${a}/read`).catch(() => {});
    }
  }, [sp]);

  // Prevent search-engine indexing of a private trip link.
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => document.head.removeChild(m);
  }, []);

  const load = useCallback(() => {
    api
      .get(`/journeys/share/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setData({ error: e?.response?.status === 410 ? "expired" : "notfound" }));
  }, [token]);

  useEffect(() => {
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [load]);

  // 1s tick keeps the ETA countdown and the freshness label updating live.
  useEffect(() => {
    if (!data || data.error || data.ended) return;
    setRemainingS(Math.max(0, (data.eta_remaining_min || 0) * 60));
    const t = setInterval(() => {
      setRemainingS((s) => (s == null ? s : Math.max(0, s - 1)));
      forceTick((n) => n + 1);
    }, 1000);
    return () => clearInterval(t);
  }, [data]);

  if (data === undefined)
    return <div className="min-h-screen bg-[#FFF7E6] flex items-center justify-center text-[#111827]/50">Loading trip…</div>;

  if (data.error)
    return (
      <div className="min-h-screen bg-[#FFF7E6] flex flex-col items-center justify-center px-6 text-center" data-testid={data.error === "expired" ? "share-expired" : "share-notfound"}>
        <ShieldCheck className="w-10 h-10 text-[#111827]/30 mb-3" />
        <p className="font-bold text-lg">{data.error === "expired" ? "This trip link has expired" : "This trip link isn't active"}</p>
        <p className="text-sm text-[#111827]/50 mt-1">The journey may have ended, or sharing was turned off.</p>
      </div>
    );

  if (data.ended)
    return (
      <div className="min-h-screen bg-[#FFF7E6] flex flex-col items-center justify-center px-6 text-center" data-testid="share-ended">
        <div className="w-16 h-16 rounded-full bg-[#16A34A]/12 flex items-center justify-center mb-3"><Check className="w-9 h-9 text-[#16A34A]" /></div>
        <p className="font-black text-xl">{data.status === "arrived" ? `${data.traveller} arrived safely` : "Journey ended"}</p>
        <p className="text-sm text-[#111827]/50 mt-1">Live sharing has stopped. This link is now closed.</p>
      </div>
    );

  const emergency = data.emergency; // silent SOS is never surfaced here
  const accent = emergency ? "#DC2626" : data.escalation_level > 0 ? "#F97316" : "#0E7C66";
  const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const loc = data.location;
  const markers = [];
  if (loc) markers.push({ id: "you", lat: loc.lat, lng: loc.lng, type: "you", pulse: true, label: data.traveller, sub: loc.precise ? "Live location" : "Approximate area" });
  if (data.responder?.lat) markers.push({ id: "r", lat: data.responder.lat, lng: data.responder.lng, type: "responder", letter: data.responder.name?.[0], label: data.responder.name });

  const checkinLabel = { yes: "Checked in — safe", need_help: "Asked for help", skip: "Check-in skipped" }[data.checkin_status];

  return (
    <div className="min-h-screen bg-[#FFF7E6]" data-testid="share-view">
      <div className="max-w-lg mx-auto">
        <div className="px-5 pt-10 pb-8 rounded-b-[2rem] text-white relative overflow-hidden" style={{ background: accent }}>
          <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10" />
          <div className="relative flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center">
              {emergency ? <BellRing className="w-7 h-7" /> : <ShieldCheck className="w-7 h-7" />}
            </div>
            <div>
              <p className="text-white/80 text-xs font-bold uppercase tracking-wider">Guardian AI by HumNest</p>
              <h1 className="text-xl font-black font-head leading-tight">{data.traveller} is on the way</h1>
            </div>
          </div>
        </div>

        <div className="px-5 -mt-4 space-y-4 pb-10">
          {emergency && (
            <div data-testid="share-emergency" className="bg-[#DC2626] text-white rounded-2xl p-4 flex items-center gap-2 font-bold">
              <BellRing className="w-5 h-5" /> {data.traveller} has triggered an SOS. Please try to reach them.
            </div>
          )}

          <div className="bg-white rounded-3xl p-5 border border-[#111827]/5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-[#111827]/45 uppercase tracking-wide">Status</p>
                <p className="font-black text-lg" data-testid="share-status" style={{ color: accent }}>{data.status_label}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-black tabular-nums" data-testid="share-eta">{remainingS != null ? mmss(remainingS) : "--:--"}</p>
                <p className="text-xs text-[#111827]/50">ETA</p>
              </div>
            </div>
            <div className="mt-3 h-2 rounded-full bg-[#111827]/8 overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${Math.round((data.progress || 0) * 100)}%`, background: accent }} />
            </div>
            <div className="mt-4 space-y-2 text-sm">
              <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#111827]/40" /> Heading to <span className="font-bold">{data.destination}</span></p>
              <p className="flex items-center gap-2 capitalize"><Navigation className="w-4 h-4 text-[#111827]/40" /> By {data.mode}</p>
              {checkinLabel && <p className="flex items-center gap-2"><Check className="w-4 h-4 text-[#0E7C66]" /> {checkinLabel}</p>}
              {data.escalation_label && <p className="flex items-center gap-2 text-[#F97316] font-semibold"><Clock className="w-4 h-4" /> {data.escalation_label}</p>}
              <p className="text-xs text-[#111827]/40">Updated {timeAgo(data.last_update)}</p>
            </div>
          </div>

          {(data.battery_low || data.online === false) && (
            <div data-testid="share-warning" className="bg-[#F59E0B]/15 text-[#92400e] rounded-2xl p-3 flex items-center gap-2 text-sm font-semibold">
              {data.online === false ? <WifiOff className="w-4 h-4" /> : <BatteryLow className="w-4 h-4" />}
              {data.online === false ? "Their phone briefly lost signal — showing last known position." : "Their phone battery is low."}
            </div>
          )}

          {loc ? (
            <>
              <LiveMap center={[loc.lat, loc.lng]} zoom={loc.precise ? 15 : 13} height={200} markers={markers} testId="share-map" />
              <p className="text-xs text-[#111827]/45 text-center">{loc.precise ? "Sharing exact live location" : "Sharing approximate area only (not exact address)"}</p>
            </>
          ) : (
            <div className="bg-white rounded-3xl p-6 text-center text-sm text-[#111827]/50 border border-[#111827]/5" data-testid="share-map-empty">
              <MapPin className="w-6 h-6 mx-auto mb-2 text-[#111827]/30" /> Location not being shared right now.
            </div>
          )}

          <LocationPanel loc={loc} lastUpdate={data.last_update} travellerName={data.traveller} />

          {data.responder && (
            <div className="bg-white rounded-3xl p-4 border border-[#111827]/5 flex items-center gap-3" data-testid="share-responder">
              <div className="w-11 h-11 rounded-full bg-[#F97316] text-white flex items-center justify-center font-black">{data.responder.name?.[0]}</div>
              <div className="flex-1"><p className="font-bold text-sm flex items-center gap-1">{data.responder.name} <ShieldCheck className="w-3.5 h-3.5 text-[#0E7C66]" /></p><p className="text-xs text-[#111827]/50">Verified guardian responding · ~{data.responder.eta_min} min away</p></div>
            </div>
          )}

          <p className="text-xs text-[#111827]/45 text-center px-4 flex items-start gap-1.5">
            <ShieldCheck className="w-4 h-4 shrink-0 text-[#0E7C66]" />
            You're seeing a private, read-only trip link. It stops sharing the moment {data.traveller} arrives or turns it off. HumNest coordinates neighbours and does not contact police or ambulance.
          </p>
        </div>
      </div>
    </div>
  );
}
